import React, { createContext, useContext, type ReactNode, useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { useAuthenticator } from '@aws-amplify/ui-react'

type AuthType = 'edlink' | 'cognito'

export interface AuthenticatedContextValue {
  isLoading: boolean
  isAuthed: boolean
  cognitoIdpContext: any
  isEdLinkSessionExists: boolean
  isCognitoSessionExists: boolean
}

const AuthenticatedContext = createContext<AuthenticatedContextValue | null>(null)

export function AuthenticatedProvider ({ children }: { children: ReactNode }): React.ReactElement {
  const cognitoIdpContext = useAuthenticator((context) => [context.route, context.user])
  const [edlinkSessionExists, setEdlinkSessionExists] = useState(() => {
    return Cookies.get('edlink_access_token_exists') != null && Cookies.get('edlink_refresh_token_exists') != null
  })

  useEffect(() => {
    const handler = (): void => {
      setEdlinkSessionExists(
        Cookies.get('edlink_access_token_exists') != null && Cookies.get('edlink_refresh_token_exists') != null
      )
    }
    window.addEventListener('edlink-logout', handler)
    return () => { window.removeEventListener('edlink-logout', handler) }
  }, [])

  // Also check cookies on mount and when localStorage changes
  useEffect(() => {
    const handler = (): void => {
      setEdlinkSessionExists(
        Cookies.get('edlink_access_token_exists') != null && Cookies.get('edlink_refresh_token_exists') != null
      )
    }
    window.addEventListener('storage', handler)
    return () => { window.removeEventListener('storage', handler) }
  }, [])

  const authTypeRaw = localStorage.getItem('authType')
  const isEdLinkSessionExists = edlinkSessionExists
  const isCognitoSessionExists =
    cognitoIdpContext.authStatus === 'authenticated' &&
    !!cognitoIdpContext.user?.userId &&
    !!cognitoIdpContext.user?.username
  const isAuthed = authTypeRaw === 'edlink' ? isEdLinkSessionExists : isCognitoSessionExists
  const isLoading =
    authTypeRaw === 'edlink'
      ? false // cookie check is synchronous
      : cognitoIdpContext.authStatus !== 'authenticated' &&
        cognitoIdpContext.authStatus !== 'unauthenticated'
  const value = {
    isLoading,
    isAuthed,
    cognitoIdpContext,
    isEdLinkSessionExists,
    isCognitoSessionExists
  }
  return (
    <AuthenticatedContext.Provider value={value}>
      {children}
    </AuthenticatedContext.Provider>
  )
}

export function useAuthenticatedContext (): AuthenticatedContextValue {
  const ctx = useContext(AuthenticatedContext)
  if (!ctx) {
    throw new Error('useAuthenticatedContext must be used within <AuthenticatedProvider />')
  }
  return ctx
}
